const LIVE_STAGING_ORIGIN = 'https://erevis-live-staging.bigworldcapitalgroup.com'
export const CHART_EXPORT_DEBUG_STORAGE_KEY = 'erevis.chartExportDebug'

let nextChartExportId = 1

function readSearchParams(locationObj) {
  try {
    return new URLSearchParams(locationObj?.search || '')
  } catch {
    return new URLSearchParams()
  }
}

export function createChartExportId(prefix = 'trade-chart') {
  const normalizedPrefix = String(prefix || 'trade-chart')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'trade-chart'
  const id = `${normalizedPrefix}-${nextChartExportId}`
  nextChartExportId += 1
  return id
}

export function isChartExportSafeOrigin(origin) {
  if (origin === LIVE_STAGING_ORIGIN) return true
  try {
    const url = new URL(origin)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    return ['localhost', '127.0.0.1', '[::1]', '::1'].includes(url.hostname)
  } catch {
    return false
  }
}

export function isChartExportDebugEnabled({
  locationObj = typeof window === 'undefined' ? null : window.location,
  localStorageObj = typeof window === 'undefined' ? null : window.localStorage,
} = {}) {
  const origin = locationObj?.origin || ''
  if (!isChartExportSafeOrigin(origin)) return false
  const params = readSearchParams(locationObj)
  if (params.get('chartExport') === '1' || params.get('chartExportDebug') === '1') return true
  try {
    return localStorageObj?.getItem(CHART_EXPORT_DEBUG_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function finiteNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function isoFromEpochSeconds(epochSeconds) {
  const seconds = finiteNumber(epochSeconds)
  if (seconds == null) return null
  const date = new Date(seconds * 1000)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function isoFromEpochMs(epochMs) {
  const ms = finiteNumber(epochMs)
  if (ms == null) return null
  const date = new Date(ms)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export function normalizeChartExportBar(bar, { index = 0, activeBarTime = null } = {}) {
  const time = finiteNumber(bar?.time)
  const rawEpochMs = finiteNumber(bar?.rawEpochMs)
  const isActive = time != null && activeBarTime != null && time === Number(activeBarTime)
  return {
    index,
    time,
    timeUnix: time,
    timeIso: isoFromEpochSeconds(time),
    rawEpochMs,
    rawIso: isoFromEpochMs(rawEpochMs),
    open: finiteNumber(bar?.open),
    high: finiteNumber(bar?.high),
    low: finiteNumber(bar?.low),
    close: finiteNumber(bar?.close),
    volume: finiteNumber(bar?.volume) ?? 0,
    source: isActive
      ? (bar?.lastPriceOnly ? 'active-sse-last-price' : 'active-sse-bar')
      : 'rendered-history',
    finality: isActive ? 'forming' : 'finalized-or-rendered',
    qualityFlags: Array.isArray(bar?.qualityFlags) ? [...bar.qualityFlags] : [],
  }
}

export function buildChartExportSnapshot({
  chartId,
  symbol,
  timeframe,
  session,
  mode,
  timezone,
  chartType,
  fetchStatus,
  liveStatus,
  runtimeFanoutStatus,
  chartParityMode = false,
  bars,
  activeBarTime = null,
  generatedAt = new Date(),
} = {}) {
  const normalizedBars = (Array.isArray(bars) ? bars : [])
    .map((bar, index) => normalizeChartExportBar(bar, { index, activeBarTime }))
    .filter((bar) => bar.time != null)
  return {
    schema: 'erevis-chart-export.v1',
    chartId: chartId || null,
    generatedAt: generatedAt instanceof Date ? generatedAt.toISOString() : String(generatedAt || ''),
    metadata: {
      symbol: symbol || null,
      timeframe: timeframe || null,
      session: session || null,
      mode: mode || null,
      timezone: timezone || null,
      chartType: chartType || null,
      fetchStatus: fetchStatus || null,
      liveStatus: liveStatus || null,
      runtimeFanoutStatus: runtimeFanoutStatus || null,
      chartParityMode: Boolean(chartParityMode),
      activeBarTime: activeBarTime ?? null,
      activeBarTimeIso: isoFromEpochSeconds(activeBarTime),
    },
    count: normalizedBars.length,
    bars: normalizedBars,
  }
}

function getExportRegistry(windowObj) {
  if (!windowObj.__EREVIS_CHART_EXPORTS__ || !(windowObj.__EREVIS_CHART_EXPORTS__.providers instanceof Map)) {
    windowObj.__EREVIS_CHART_EXPORTS__ = {
      providers: new Map(),
      latestChartId: null,
    }
  }
  return windowObj.__EREVIS_CHART_EXPORTS__
}

function installGlobalExportFunction(windowObj, registry) {
  windowObj.__EREVIS_CHART_EXPORT__ = (chartId = null) => {
    const id = chartId || registry.latestChartId
    if (!id) return null
    const provider = registry.providers.get(id)
    if (typeof provider !== 'function') return null
    return provider()
  }
}

export function registerChartExportSnapshot(chartId, snapshotProvider, {
  windowObj = typeof window === 'undefined' ? null : window,
} = {}) {
  if (!windowObj || !chartId || typeof snapshotProvider !== 'function') return () => {}
  const registry = getExportRegistry(windowObj)
  registry.providers.set(chartId, snapshotProvider)
  registry.latestChartId = chartId
  installGlobalExportFunction(windowObj, registry)

  return () => {
    const current = getExportRegistry(windowObj)
    current.providers.delete(chartId)
    if (current.latestChartId === chartId) {
      current.latestChartId = Array.from(current.providers.keys()).at(-1) || null
    }
    installGlobalExportFunction(windowObj, current)
  }
}
